import { apiClient } from './api-client.js';
import type { LoginResult, RegisterResult, ResendResult, SignalRToken, UserInfo } from '../models/auth.js';

/**
 * Client-side façade over the `/api/auth/*` endpoints. All calls rely on the Identity auth cookie
 * (sent automatically by the API client), so there is no token handling here except for the
 * short-lived SignalR access token, which the SignalR client fetches on demand.
 */
export class AuthService {
  /**
   * Creates an account. Sign-up requires email confirmation, so this establishes no session and
   * resolves with a {@link RegisterResult} indicating the user should check their email.
   */
  register(email: string, password: string, displayName?: string): Promise<RegisterResult> {
    return apiClient.post<RegisterResult>('/api/auth/register', { email, password, displayName });
  }

  /**
   * Signs in with email + password. Resolves with a {@link LoginResult}: when the account has 2FA
   * enabled the result has `requiresTwoFactor: true` and the caller must follow up with
   * {@link loginWith2fa} or {@link loginWithRecoveryCode}; otherwise `user` is populated.
   */
  login(email: string, password: string, rememberMe = true): Promise<LoginResult> {
    return apiClient.post<LoginResult>('/api/auth/login', { email, password, rememberMe });
  }

  /**
   * Completes a 2FA challenge with a time-based authenticator code. `rememberMachine` skips 2FA on
   * this device next time. Must follow a {@link login} that returned `requiresTwoFactor: true`.
   */
  loginWith2fa(code: string, rememberMe = true, rememberMachine = false): Promise<LoginResult> {
    return apiClient.post<LoginResult>('/api/auth/login-2fa', { code, rememberMe, rememberMachine });
  }

  /** Completes a 2FA challenge with a single-use recovery code. */
  loginWithRecoveryCode(recoveryCode: string): Promise<LoginResult> {
    return apiClient.post<LoginResult>('/api/auth/login-recovery', { recoveryCode });
  }

  /** Ends the session. */
  logout(): Promise<void> {
    return apiClient.post<void>('/api/auth/logout');
  }

  /** Returns the current user, or null if not signed in. */
  async me(): Promise<UserInfo | null> {
    try {
      return await apiClient.get<UserInfo>('/api/auth/me');
    } catch {
      return null;
    }
  }

  /** Starts a password reset. Always resolves (the server never reveals whether the email exists). */
  forgotPassword(email: string): Promise<void> {
    return apiClient.post<void>('/api/auth/forgot-password', { email });
  }

  /** Completes a password reset using the token from the reset email. */
  resetPassword(email: string, token: string, newPassword: string): Promise<void> {
    return apiClient.post<void>('/api/auth/reset-password', { email, token, newPassword });
  }

  /** Re-sends the email-confirmation message. Always resolves (the server never reveals account state). */
  resendConfirmation(email: string): Promise<ResendResult> {
    return apiClient.post<ResendResult>('/api/auth/resend-confirmation', { email });
  }

  /** Obtains a fresh short-lived JWT for authenticating a SignalR connection. */
  getSignalRToken(): Promise<SignalRToken> {
    return apiClient.post<SignalRToken>('/api/auth/signalr-token');
  }
}

/** Shared default instance used by components unless one is injected for testing. */
export const authService = new AuthService();
